import type { ComponentType, ReactNode, SVGProps } from "react"
import { useState } from "react"
import {
	IconCheckFill18 as CheckFilled,
	IconCircleCheckFill18 as CircleCheckFilled,
} from "nucleo-ui-fill-18"
import {
	IconArrowRightOutline18 as ArrowRight,
	IconAtom2Outline18 as AtomIcon,
	IconBookOpen4Outline18 as BookIcon,
	IconSquaresConnectedOutline18 as ConnectedIcon,
	IconEnvelopeOutline18 as EnvelopeIcon,
	IconMessagesOutline18 as MessagesIcon,
	IconUsersOutline18 as UsersIcon,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import {
	Badge,
	Button,
	IntercomIcon,
	LinearIcon,
	SlackIcon,
} from "@productlane/ui"
import { TeamsIcon } from "@productlane/ui/src/icons/project/ms-teams-icon"

import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"

type Glyph = ComponentType<SVGProps<SVGSVGElement>>

interface Channel {
	name: string
	Icon: Glyph
	isConnected: boolean
	href: string
}

const CHANNELS: Channel[] = [
	{
		name: "Shared email",
		Icon: EnvelopeIcon,
		isConnected: true,
		href: "/settings/emails",
	},
	{
		name: "Widget",
		Icon: MessagesIcon,
		isConnected: false,
		href: "/settings/widget",
	},
	{
		name: "Slack",
		Icon: SlackIcon,
		isConnected: false,
		href: "/settings/integrations/slack",
	},
	{
		name: "Teams",
		Icon: TeamsIcon,
		isConnected: false,
		href: "/settings/integrations/teams",
	},
	{
		name: "Intercom",
		Icon: IntercomIcon,
		isConnected: false,
		href: "/settings/integrations/intercom",
	},
	{
		name: "Portal",
		Icon: BookIcon,
		isConnected: false,
		href: "/settings/portal",
	},
]

type StepState = "done" | "active" | "todo"

interface Step {
	id: string
	title: string
	body: string
	cta: string
	Icon: Glyph
	state: StepState
	minutes: string
}

const STEPS: Step[] = [
	{
		id: "channels",
		title: "Connect a channel",
		body: "Email, widget, Slack, Teams, Intercom. Every message arrives in one inbox.",
		cta: "Connect channels",
		Icon: ConnectedIcon,
		state: "active",
		minutes: "2 min",
	},
	{
		id: "docs",
		title: "Bring in your docs",
		body: "Import your help center or point us at your repo. Your answers live here.",
		cta: "Import docs",
		Icon: BookIcon,
		state: "todo",
		minutes: "3 min",
	},
	{
		id: "agent",
		title: "Turn on the AI agent",
		body: "It answers from your docs and hands a thread to you when it should.",
		cta: "Open agent settings",
		Icon: AtomIcon,
		state: "todo",
		minutes: "1 min",
	},
	{
		id: "linear",
		title: "Connect Linear",
		body: "File an issue from a thread, then reply to everyone who asked when it ships.",
		cta: "Connect Linear",
		Icon: LinearIcon,
		state: "todo",
		minutes: "1 min",
	},
	{
		id: "team",
		title: "Invite your team",
		body: "Assign threads, share drafts, and split the inbox between you.",
		cta: "Invite teammates",
		Icon: UsersIcon,
		state: "done",
		minutes: "1 min",
	},
]

const DONE_COUNT = STEPS.filter((step) => step.state === "done").length

function StepMark({ state }: { state: StepState }) {
	return (
		<span
			aria-hidden="true"
			className={cn(
				"flex h-5 w-5 flex-none items-center justify-center rounded-full border",
				state === "done"
					? "border-green-500 bg-green-500 text-white"
					: state === "active"
						? "border-gray-900 dark:border-gray-100"
						: "border-gray-300 dark:border-gray-600",
			)}
		>
			{state === "done" && <CheckFilled className="h-3 w-3 flex-none" />}
			{state === "active" && (
				<span className="h-1.5 w-1.5 flex-none rounded-full bg-gray-900 dark:bg-gray-100" />
			)}
		</span>
	)
}

function ProgressBar({ value, total }: { value: number; total: number }) {
	return (
		<div className="h-1 w-28 flex-none overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
			<div
				className="h-full rounded-full bg-green-500"
				style={{ width: `${(value / total) * 100}%` }}
			/>
		</div>
	)
}

function TrialFooter() {
	return (
		<div className="flex items-center gap-3 rounded-xl bg-white p-3 shadow-sm ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
			<div className="min-w-0 flex-1">
				<div className="text-[13px] text-primary">
					7 days left in your trial
				</div>
				<div className="mt-0.5 text-xs text-secondary">
					Every feature is on. Pick a plan to keep what you set up.
				</div>
			</div>
			<Button variant="outline" size="sm">
				Pick a plan
			</Button>
		</div>
	)
}

// 1. Guided home. A centered column of steps with a live workspace summary beside it.
export function GuidedHome() {
	return (
		<div className="bg-gray-50 p-6 dark:bg-gray-900">
			<div className="mb-5">
				<div className="text-[15px] font-semibold text-primary">
					Welcome, Raphael
				</div>
				<div className="mt-0.5 text-[13px] text-secondary">
					Five steps to a working inbox with an agent in front of it.
				</div>
			</div>
			<div className="flex gap-4">
				<div className="min-w-0 flex-1 space-y-3">
					<div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
						<div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700/80">
							<span className="flex-1 text-[13px] font-medium text-primary">
								Set up your workspace
							</span>
							<span className="text-xs tabular-nums text-tertiary">
								{DONE_COUNT} of {STEPS.length} done
							</span>
							<ProgressBar value={DONE_COUNT} total={STEPS.length} />
						</div>
						<div>
							{STEPS.map((step) => (
								<div
									key={step.id}
									className="flex items-start gap-3 border-b border-gray-200 px-4 py-3.5 last:border-b-0 dark:border-gray-700/80"
								>
									<StepMark state={step.state} />
									<div className="min-w-0 flex-1">
										<div className="flex items-center gap-2">
											<span
												className={cn(
													"text-[13px]",
													step.state === "done"
														? "text-tertiary line-through"
														: "text-primary",
												)}
											>
												{step.title}
											</span>
											<Badge variant="transparent" size="sm">
												{step.minutes}
											</Badge>
										</div>
										<div className="mt-0.5 text-xs text-secondary">
											{step.body}
										</div>
										{step.id === "channels" && (
											<div className="mt-2.5 flex flex-wrap gap-1.5">
												{CHANNELS.map((channel) => (
													<ChannelChip key={channel.name} channel={channel} />
												))}
											</div>
										)}
									</div>
									{step.state !== "done" && (
										<Button
											variant={step.state === "active" ? "default" : "outline"}
											size="sm"
											className="flex-none"
										>
											{step.state === "active" ? step.cta : "Open"}
										</Button>
									)}
								</div>
							))}
						</div>
					</div>
					<TrialFooter />
				</div>
				<div className="w-[240px] flex-none space-y-3">
					<SummaryCard />
				</div>
			</div>
		</div>
	)
}

function ChannelChip({ channel }: { channel: Channel }) {
	const { Icon } = channel
	return (
		<span
			className={cn(
				"flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs ring-1 ring-inset",
				channel.isConnected
					? "bg-green-50 text-green-700 ring-green-500/30 dark:bg-green-500/10 dark:text-green-400 dark:ring-green-500/20"
					: "bg-white text-secondary ring-black/10 hover:bg-gray-100 dark:bg-gray-800 dark:ring-white/10 dark:hover:bg-gray-750",
			)}
		>
			<Icon className="h-3.5 w-3.5 flex-none" />
			{channel.name}
			{channel.isConnected && (
				<CircleCheckFilled className="h-3 w-3 flex-none text-green-500" />
			)}
		</span>
	)
}

function SummaryCard() {
	return (
		<div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
			<div className="text-[13px] font-medium text-primary">Your workspace</div>
			<dl className="mt-3 space-y-3">
				<SummaryRow label="Channels" value="1 of 6 connected" />
				<SummaryRow label="Help center" value="No articles yet" />
				<SummaryRow label="AI agent" value="Off" />
				<SummaryRow label="Team" value="3 people" />
			</dl>
		</div>
	)
}

function SummaryRow({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<dt className="text-xs text-tertiary">{label}</dt>
			<dd className="mt-0.5 text-[13px] text-primary">{value}</dd>
		</div>
	)
}

// 2. Channels first. The channel grid is the hero, the rest of setup sits under it.
export function ChannelFirstHome() {
	return (
		<div className="bg-gray-50 p-6 dark:bg-gray-900">
			<div className="mb-5 flex items-end justify-between gap-4">
				<div>
					<div className="text-[15px] font-semibold text-primary">
						Where do your customers write?
					</div>
					<div className="mt-0.5 text-[13px] text-secondary">
						Pick one to start. Threads from every channel arrive in the same
						inbox.
					</div>
				</div>
				<span className="text-xs tabular-nums text-tertiary">
					{DONE_COUNT}/{STEPS.length} set up
				</span>
			</div>
			<div className="grid grid-cols-3 gap-3">
				{CHANNELS.map((channel) => (
					<ChannelTile key={channel.name} channel={channel} />
				))}
			</div>
			<div className="mt-6 text-xs font-medium text-secondary">Then</div>
			<div className="mt-2 grid grid-cols-4 gap-3">
				{STEPS.filter((step) => step.id !== "channels").map((step) => {
					const { Icon } = step
					return (
						<div
							key={step.id}
							className="rounded-xl bg-white p-3.5 shadow-sm ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10"
						>
							<div className="flex items-center gap-2">
								<Icon className="h-4 w-4 flex-none text-secondary" />
								<StepMark state={step.state} />
							</div>
							<div className="mt-2.5 text-[13px] text-primary">
								{step.title}
							</div>
							<div className="mt-1 text-xs text-secondary">{step.body}</div>
						</div>
					)
				})}
			</div>
			<div className="mt-4">
				<TrialFooter />
			</div>
		</div>
	)
}

function ChannelTile({ channel }: { channel: Channel }) {
	const { Icon } = channel
	return (
		<div className="flex items-center gap-3 rounded-xl bg-white p-3.5 shadow-sm ring-1 ring-black/5 hover:bg-gray-50 dark:bg-gray-850 dark:ring-white/10 dark:hover:bg-gray-800">
			<span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
				<Icon className="h-[18px] w-[18px] flex-none text-secondary" />
			</span>
			<div className="min-w-0 flex-1">
				<div className="text-[13px] text-primary">{channel.name}</div>
				<div className="mt-0.5 text-xs text-tertiary">
					{channel.isConnected ? "Connected" : "Takes a minute"}
				</div>
			</div>
			{channel.isConnected ? (
				<CircleCheckFilled className="h-4 w-4 flex-none text-green-500" />
			) : (
				<ArrowRight className="h-4 w-4 flex-none text-tertiary" />
			)}
		</div>
	)
}

// 3. Inbox takeover. The setup list fills the reading pane until a thread arrives.
export function InboxTakeover() {
	return (
		<div className="flex h-[420px] overflow-hidden rounded-xl bg-white ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
			<div className="w-[220px] flex-none border-r border-gray-200 bg-gray-50 p-3 dark:border-gray-700/80 dark:bg-gray-850">
				<div className="text-xs font-medium text-secondary">Inbox</div>
				<div className="mt-3 space-y-2">
					{[0, 1, 2].map((row) => (
						<div key={row} className="space-y-1.5 rounded-md px-1 py-1">
							<div className="h-2 w-3/4 rounded bg-gray-200 dark:bg-gray-700" />
							<div className="h-2 w-1/2 rounded bg-gray-150 dark:bg-gray-750" />
						</div>
					))}
				</div>
			</div>
			<div className="min-w-0 flex-1 overflow-y-auto p-8 pb-10">
				<div className="mx-auto max-w-[420px]">
					<div className="text-[15px] font-semibold text-primary">
						Your inbox is ready for its first thread
					</div>
					<div className="mt-1 text-[13px] text-secondary">
						Finish these and customers reach you here.
					</div>
					<ol className="mt-5">
						{STEPS.map((step, index) => (
							<li key={step.id} className="relative flex gap-3 pb-5 last:pb-0">
								{index < STEPS.length - 1 && (
									<span
										aria-hidden="true"
										className="absolute left-[9px] top-6 h-[calc(100%-1.25rem)] w-px bg-gray-200 dark:bg-gray-700"
									/>
								)}
								<StepMark state={step.state} />
								<div className="min-w-0 flex-1 pt-px">
									<div
										className={cn(
											"text-[13px]",
											step.state === "todo" ? "text-secondary" : "text-primary",
										)}
									>
										{step.title}
									</div>
									{step.state === "active" && (
										<>
											<div className="mt-1 text-xs text-secondary">
												{step.body}
											</div>
											<div className="mt-2.5 flex flex-wrap gap-1.5">
												{CHANNELS.slice(0, 4).map((channel) => (
													<ChannelChip key={channel.name} channel={channel} />
												))}
											</div>
											<Button size="sm" className="mt-3">
												{step.cta}
											</Button>
										</>
									)}
								</div>
							</li>
						))}
					</ol>
					<div className="mt-6 border-t border-gray-200 pt-3 text-xs text-tertiary dark:border-gray-700/80">
						7 days left in your trial. Every feature is on until then.
					</div>
				</div>
			</div>
		</div>
	)
}

// 4. Sidebar panel. The popover that ships today, grown into a panel with actions.
export function SidebarPanel() {
	return (
		<div className="flex h-[440px] overflow-hidden rounded-xl bg-gray-50 ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
			<div className="flex w-[280px] flex-none flex-col border-r border-gray-200 bg-white dark:border-gray-700/80 dark:bg-gray-850">
				<div className="flex items-center gap-2 px-3 py-3">
					<span className="h-5 w-5 flex-none rounded bg-gray-200 dark:bg-gray-700" />
					<span className="text-[13px] text-primary">Productlane</span>
				</div>
				<div className="flex-1 space-y-1 px-3">
					{["Inbox", "Threads", "Help center", "Roadmap"].map((item) => (
						<div
							key={item}
							className="rounded px-2 py-1 text-[13px] text-secondary hover:bg-gray-150 dark:hover:bg-gray-800"
						>
							{item}
						</div>
					))}
				</div>
				<div className="p-2">
					<div className="rounded-lg bg-gray-50 p-2 ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
						<div className="flex items-center gap-2 px-1 pb-1.5">
							<span className="flex-1 text-xs text-secondary">Get started</span>
							<ProgressBar value={DONE_COUNT} total={STEPS.length} />
						</div>
						{STEPS.map((step) => (
							<div
								key={step.id}
								className="flex items-start gap-2 rounded px-1.5 py-1.5 hover:bg-gray-150 dark:hover:bg-gray-800"
							>
								<StepMark state={step.state} />
								<div className="min-w-0 flex-1">
									<div
										className={cn(
											"text-[13px]",
											step.state === "done" ? "text-tertiary" : "text-primary",
										)}
									>
										{step.title}
									</div>
									{step.state === "active" && (
										<div className="mt-0.5 text-xxs text-secondary">
											{step.body}
										</div>
									)}
								</div>
							</div>
						))}
						<div className="mt-1.5 border-t border-gray-200 px-1.5 pt-2 dark:border-gray-700/80">
							<div className="flex items-center gap-2">
								<span className="flex-1 text-xs text-secondary">
									7 days left in trial
								</span>
								<Button variant="outline" size="xs">
									Pick a plan
								</Button>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="flex min-w-0 flex-1 items-center justify-center p-6">
				<div className="text-center text-xs text-tertiary">
					The app carries on behind the panel
				</div>
			</div>
		</div>
	)
}

// 5. One step at a time. The others stay out of sight until this one is done.
export function FocusStepper() {
	const [index, setIndex] = useState(0)
	const step = STEPS[index] ?? STEPS[0]
	if (!step) return null
	const { Icon } = step

	return (
		<div className="bg-gray-50 p-8 dark:bg-gray-900">
			<div className="mx-auto max-w-[480px]">
				<div className="flex items-center justify-center gap-1.5">
					{STEPS.map((item, itemIndex) => (
						<span
							key={item.id}
							aria-hidden="true"
							className={cn(
								"h-1 w-8 flex-none rounded-full",
								itemIndex < index
									? "bg-green-500"
									: itemIndex === index
										? "bg-gray-900 dark:bg-gray-100"
										: "bg-gray-200 dark:bg-gray-700",
							)}
						/>
					))}
				</div>
				<div className="mt-6 rounded-xl bg-white p-6 text-center shadow-sm ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
					<span className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-800">
						<Icon className="h-5 w-5 flex-none text-secondary" />
					</span>
					<div className="mt-3 text-[15px] font-semibold text-primary">
						{step.title}
					</div>
					<div className="mt-1.5 text-[13px] text-secondary">{step.body}</div>
					{step.id === "channels" && (
						<div className="mt-4 flex flex-wrap justify-center gap-1.5">
							{CHANNELS.map((channel) => (
								<ChannelChip key={channel.name} channel={channel} />
							))}
						</div>
					)}
					<div className="mt-5 flex items-center justify-center gap-2">
						<Button size="lg">{step.cta}</Button>
						<Button
							variant="ghost"
							size="lg"
							onClick={() => {
								setIndex((current) => (current + 1) % STEPS.length)
							}}
						>
							Later
						</Button>
					</div>
				</div>
				<div className="mt-3 text-center text-xs text-tertiary">
					Step {index + 1} of {STEPS.length}. 7 days left in your trial.
				</div>
			</div>
		</div>
	)
}

export function OnboardingHomeDesignPage() {
	if (!isDevEnvironment) return <NotFound />

	return (
		<div className="h-full overflow-y-auto bg-gray-100 dark:bg-gray-950">
			<div className="mx-auto max-w-[1080px] px-6 py-10 pb-24">
				<div className="mb-8">
					<div className="text-lg font-semibold text-primary">
						Trial setup screen, five options
					</div>
					<div className="mt-1 text-sm text-secondary">
						Each one carries the same five steps, with the channel step drawn
						out. The difference is where the list lives and how much of it you
						see at once.
					</div>
				</div>
				<div className="space-y-12">
					<Variant
						label="1. Guided home"
						note="A home screen of its own. Every step visible, the channel row inline, a summary of what the workspace has so far."
					>
						<GuidedHome />
					</Variant>
					<Variant
						label="2. Channels first"
						note="The channel grid is the screen. Everything else shrinks to four cards underneath."
					>
						<ChannelFirstHome />
					</Variant>
					<Variant
						label="3. Inbox takeover"
						note="No new screen. The setup list fills the reading pane while the inbox is empty, and only the active step opens up."
					>
						<InboxTakeover />
					</Variant>
					<Variant
						label="4. Sidebar panel"
						note="The popover that ships today, with a progress bar, the active step explained, and the trial line folded in."
					>
						<SidebarPanel />
					</Variant>
					<Variant
						label="5. One step at a time"
						note="A single card. Press Later to see the next step."
					>
						<FocusStepper />
					</Variant>
				</div>
			</div>
		</div>
	)
}

function Variant({
	label,
	note,
	children,
}: {
	label: string
	note: string
	children: ReactNode
}) {
	return (
		<div className="space-y-2">
			<div className="text-xs font-medium text-primary">{label}</div>
			<div className="text-xxs text-tertiary">{note}</div>
			<div className="overflow-hidden rounded-xl ring-1 ring-black/5 dark:ring-white/10">
				{children}
			</div>
		</div>
	)
}
